import { WIDGET_DEFINITIONS, buildEmbedCode } from '@flexbyt/shared';
import { cx } from '@flexbyt/widget-preview';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { CombinationScene } from '../components/mascot/scenes.js';
import { PageHero } from '../components/ui/hero.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Badge } from '../components/ui/surface.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { WidgetShowcase } from '../components/WidgetShowcase.js';
import {
    capabilityFor,
    combinationFor,
    combinationPath,
    combinationsForPlatform,
    combinationsForSource,
} from '../content/combinations.js';
import { integrationBySlug, integrationPath } from '../content/integrations.js';
import { PLATFORM_CONTENT } from '../content/platforms.js';
import { sampleSetForSource } from '../content/sample-sets.js';
import {
    WIDGET_BUILDER_PATH,
    WIDGET_COUNT_WORD,
    generatorPath,
    widgetContentByType,
    widgetPath,
} from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { breadcrumbSchema, faqSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';

/**
 * "Google reviews widget for Webflow" — one page, purpose-built.
 *
 * ===========================================================================
 * What makes this more than a template with two nouns in it
 * ===========================================================================
 * Three things, and none of them is authored prose:
 *
 *  - the **installation steps** come from `buildInstallGuide`, the function the
 *    dashboard's own install sheet calls. So a WordPress visitor is shown a
 *    shortcode and a Wix visitor is shown the Custom Element flow, because that
 *    is what each of them will actually be given.
 *  - the **embed snippet** comes from `buildEmbedCode`, which is why WordPress
 *    gets `[rj_masonry]` rather than a script tag — a script pasted into a
 *    Custom HTML block is stripped by many themes, and a marketing page that
 *    showed one would mislead people on the platform least able to debug it.
 *  - the **claim** comes from `PLATFORM_CONTENT[platform].depth`, so "native
 *    Wix app" and "works on Squarespace" cannot be swapped by accident.
 *
 * What is written per combination is the half a machine cannot produce: why
 * somebody with these reviews and this website has this particular problem.
 */
export const Route = createFileRoute('/integrations/$slug/$platform')({
  loader: ({ params }) => {
    const combination = combinationFor(params.slug, params.platform);
    /*
      A 404 for an unpublished pair rather than a generated page.

      This is the whole defence against thin programmatic SEO: the route could
      serve seventy-two combinations and serves thirteen, because the other
      fifty-nine have nothing to say that the source page and the platform page
      do not already say better.
    */
    if (!combination) throw notFound();

    const integration = integrationBySlug(params.slug)!;
    return { combination, integration };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { combination, integration } = loaderData;
    const platform = PLATFORM_CONTENT[combination.platform];

    const { meta, links } = seo({
      title: `${integration.name} reviews widget for ${platform.name}`,
      description: `Show your ${integration.name} reviews on your ${platform.name} site. ${combination.why}`,
      path: combinationPath(combination),
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Integrations', path: '/integrations' },
              { name: integration.name, path: integrationPath(integration) },
              { name: platform.name, path: combinationPath(combination) },
            ])
          ),
        },
        {
          type: 'application/ld+json',
          children: jsonLd(faqSchema(combination.faqs)),
        },
      ],
    };
  },
  component: CombinationPage,
});

function CombinationPage() {
  const { combination, integration } = Route.useLoaderData();
  const platform = PLATFORM_CONTENT[combination.platform];
  const capability = capabilityFor(combination);
  const widget = widgetContentByType(combination.recommended)!;
  const reviews = sampleSetForSource(combination.source).reviews.slice(0, 6);
  const embedCode = buildEmbedCode({
    widgetType: combination.recommended,
    embedId: 'your-site-id',
    platform: combination.platform,
  });

  const native = capability.depth !== 'embed';

  return (
    <>
      {/* ------------------------------------------------------------ Hero */}
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Integrations', path: '/integrations' },
          { name: integration.name, path: integrationPath(integration) },
          { name: platform.name, path: combinationPath(combination) },
        ]}
        art={<CombinationScene source={integration.id} platform={platform.name} />}
      >
        <Measure>
          <div className="flex flex-wrap items-center gap-3">
            <Eyebrow>
              {integration.name} · {platform.name}
            </Eyebrow>
            {/*
              The claim, from the product. `native` is `depth !== 'embed'`, so a
              page can never call a pasted snippet an integration.
            */}
            <Badge tone={native ? 'positive' : 'neutral'}>{platform.claim}</Badge>
          </div>

          <Heading level={1} size="hero" className="mt-4">
            {integration.name} reviews widget for <span className="whitespace-nowrap"><Mark>{platform.name}</Mark>.</span>
          </Heading>
          <Text size="lead" className="mt-5">
            {combination.why}
          </Text>
        </Measure>
      </PageHero>

      {/* --------------------------------------------- What it looks like */}
      <Section size="tight" className="pt-10" aria-labelledby="preview-heading">
        <Measure>
          <Heading level={2} size="title" id="preview-heading">
            The {widget.seoName.toLowerCase()} is usually the right one here
          </Heading>
          <Text className="mt-3">{combination.recommendedWhy}</Text>
        </Measure>

        <div className="rj-canvas mt-8 rounded-xl p-4 ring-1 ring-ink-150 sm:p-8">
          <WidgetShowcase type={combination.recommended} reviews={reviews} />
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <ButtonLink
            to={generatorPath(widget)}
            onClick={() =>
              track('widget_selected', {
                widget: combination.recommended,
                source: combination.source,
                platform: combination.platform,
                surface: `combo-${integration.slug}-${platform.slug}`,
              })
            }
          >
            Build this widget free
          </ButtonLink>
          <ButtonLink to={WIDGET_BUILDER_PATH} variant="secondary">
            Try a different layout
          </ButtonLink>
        </div>

        <p className="mt-4 text-caption text-ink-500">
          Sample {integration.name} reviews, drawn by the real widget. Yours
          replace them once you connect your listing.
        </p>
      </Section>

      {/* ------------------------------------------------------- Specifics */}
      <Section tone="raised" aria-labelledby="notes-heading">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-16">
          <div>
            <Heading level={2} id="notes-heading">
              What is different about {platform.name}.
            </Heading>
            <ul className="mt-6 space-y-4">
              {combination.notes.map((note) => (
                <li key={note} className="flex gap-3">
                  <span
                    aria-hidden
                    className="mt-2 size-1.5 shrink-0 rounded-full bg-ink-300"
                  />
                  <p className="max-w-prose text-body-sm text-ink-600">{note}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-6">
            <Fact label="How it installs">{platform.install}</Fact>

            <Fact label="Layouts you can use">
              {capability.widgets.length} of ReviewsJet&rsquo;s{' '}
              {Object.keys(WIDGET_DEFINITIONS).length}
              {combination.platform === 'wix'
                ? ' — including the product widget, which needs a store and is Wix-only.'
                : ' — every layout except the product widget, which needs a Wix store.'}
            </Fact>

            {capability.shortcodes.length > 0 ? (
              <Fact label="Shortcodes">
                {capability.shortcodes
                  .map((type) => `[${WIDGET_DEFINITIONS[type].shortcode}]`)
                  .join(' ')}
                {' — the popup has none, because it is site-wide rather than placed on a page.'}
              </Fact>
            ) : null}

            {capability.automatic.length > 0 ? (
              <Fact label="Installs itself">
                The popup. ReviewsJet registers the script on your{' '}
                {platform.name} site — you only have to publish.
              </Fact>
            ) : null}

            {platform.marketplaceUrl ? (
              <Fact label="Where to get it">
                <a
                  href={platform.marketplaceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-accent-600 underline decoration-accent-200 underline-offset-[3px] hover:text-accent-700"
                >
                  {platform.name} marketplace
                </a>
              </Fact>
            ) : null}
          </div>
        </div>
      </Section>

      {/* --------------------------------------------------- Installation */}
      <Section aria-labelledby="install-heading">
        <Measure>
          <Heading level={2} id="install-heading">
            {capability.guide.heading}
          </Heading>
          {capability.guide.automatic ? (
            <Text className="mt-4">{capability.guide.automatic}</Text>
          ) : null}
        </Measure>

        <ol className="mt-10 max-w-text space-y-8">
          {capability.guide.steps.map((step, index) => (
            <li key={step.title} className="flex gap-5">
              <span className="nums shrink-0 text-body-sm font-semibold text-ink-300">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-title text-ink-950">{step.title}</p>
                <p className="mt-1.5 text-body-sm text-ink-600">{step.body}</p>
                {step.code ? (
                  <pre className="mt-3 overflow-x-auto rounded-lg bg-ink-950 p-3.5 text-micro leading-relaxed text-white/85">
                    <code>{step.code}</code>
                  </pre>
                ) : null}
              </div>
            </li>
          ))}
        </ol>

        {/*
          Only where the steps do not already contain it.

          On WordPress the guide's last step *is* "paste this shortcode", so a
          second block underneath showed the same `[rj_list …]` twice in forty
          pixels. Wix is the opposite case — its steps carry a server URL, a tag
          name and an id separately, and never the one-line embed — so the block
          earns its place there.
        */}
        {!capability.guide.steps.some((step) => step.code === embedCode) ? (
          <div className="mt-10 max-w-text">
            <p className="text-caption font-semibold text-ink-950">
              What you paste
            </p>
            <pre className="mt-2 overflow-x-auto rounded-lg bg-ink-950 p-4 text-micro leading-relaxed text-white/85">
              <code>{embedCode}</code>
            </pre>
            <p className="mt-2 text-caption text-ink-500">
              ReviewsJet fills in your site id — nothing to type.
            </p>
          </div>
        ) : null}
      </Section>

      {/* -------------------------------------------------------- FAQ */}
      <Section tone="raised" aria-labelledby="faq-heading">
        <Measure>
          <Heading level={2} id="faq-heading">
            {integration.name} and {platform.name}, specifically.
          </Heading>
        </Measure>
        <dl className="mt-10 max-w-text">
          {combination.faqs.map((faq, index) => (
            <div
              key={faq.q}
              className={cx(index > 0 && 'mt-8 border-t border-ink-150 pt-8')}
            >
              <dt className="text-title text-ink-950">{faq.q}</dt>
              <dd className="mt-2.5 text-body text-ink-600">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* ------------------------------------------------ Internal linking */}
      <Section aria-labelledby="more-heading">
        <Heading level={2} id="more-heading" size="title">
          Related
        </Heading>

        <div className="mt-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <Group title={`${integration.name} elsewhere`}>
            {combinationsForSource(combination.source)
              .filter((entry) => entry.platform !== combination.platform)
              .map((entry) => (
                <SiteLink
                  key={entry.platform}
                  to={combinationPath(entry)}
                  className={LINK}
                >
                  {integration.name} for {PLATFORM_CONTENT[entry.platform].name}
                </SiteLink>
              ))}
            <SiteLink to={integrationPath(integration)} className={LINK}>
              All {integration.name} reviews
            </SiteLink>
          </Group>

          <Group title={`Other reviews on ${platform.name}`}>
            {combinationsForPlatform(combination.platform)
              .filter((entry) => entry.source !== combination.source)
              .map((entry) => {
                const other = integrationBySlug(
                  `${entry.source}-reviews`
                );
                return other ? (
                  <SiteLink
                    key={entry.source}
                    to={combinationPath(entry)}
                    className={LINK}
                  >
                    {other.name} for {platform.name}
                  </SiteLink>
                ) : null;
              })}
            <SiteLink to={`/for/${platform.slug}`} className={LINK}>
              All reviews on {platform.name}
            </SiteLink>
          </Group>

          <Group title="Widget layouts">
            <SiteLink to={widgetPath(widget)} className={LINK}>
              About the {widget.seoName.toLowerCase()}
            </SiteLink>
            <SiteLink to="/widgets" className={LINK}>
              All {WIDGET_COUNT_WORD} layouts
            </SiteLink>
          </Group>

          <Group title="Free tools">
            <SiteLink to={generatorPath(widget)} className={LINK}>
              {widget.seoName} generator
            </SiteLink>
            <SiteLink to={WIDGET_BUILDER_PATH} className={LINK}>
              Widget builder
            </SiteLink>
          </Group>
        </div>
      </Section>

      {/* ------------------------------------------------------------ CTA */}
      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Put your {integration.name} reviews on {platform.name}.
          </Heading>
          <Text className="mt-4 text-white/70">
            Free plan, no card. Connect your listing, choose what goes live, and
            paste one line into your site.
          </Text>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl(`combo-${integration.slug}-${platform.slug}`)}
              variant="inverse"
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', {
                  source: combination.source,
                  platform: combination.platform,
                  surface: `combo-${integration.slug}-${platform.slug}`,
                })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink
              to={generatorPath(widget)}
              variant="inverse-ghost"
              size="lg"
            >
              Build one without an account
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}

const LINK =
  'block text-body-sm text-ink-600 underline-offset-[3px] hover:text-ink-950 hover:underline';

function Group({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-caption font-semibold text-ink-950">{title}</p>
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}

function Fact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-l-2 border-ink-200 pl-4">
      <p className="text-caption font-semibold text-ink-950">{label}</p>
      <p className="mt-1.5 text-body-sm text-ink-600">{children}</p>
    </div>
  );
}
